/**
 * Content Service
 * Handles content pieces, templates, platforms, and analytics
 */
import api from './api';

const contentService = {
  // ==================== Content Pieces ====================
  /**
   * Get all content pieces
   */
  getPieces: async (params = {}) => {
    const response = await api.get('/content/pieces', { params });
    return response.data;
  },

  /**
   * Get content piece by ID
   */
  getPiece: async (pieceId) => {
    const response = await api.get(`/content/pieces/${pieceId}`);
    return response.data;
  },

  /**
   * Create content piece
   */
  createPiece: async (pieceData) => {
    const response = await api.post('/content/pieces', pieceData);
    return response.data;
  },

  /**
   * Update content piece
   */
  updatePiece: async (pieceId, pieceData) => {
    const response = await api.put(`/content/pieces/${pieceId}`, pieceData);
    return response.data;
  },

  /**
   * Delete content piece
   */
  deletePiece: async (pieceId) => {
    const response = await api.delete(`/content/pieces/${pieceId}`);
    return response.data;
  },

  /**
   * Duplicate content piece
   */
  duplicatePiece: async (pieceId) => {
    const response = await api.post(`/content/pieces/${pieceId}/duplicate`);
    return response.data;
  },

  // ==================== Templates ====================
  /**
   * Get all templates
   */
  getTemplates: async (params = {}) => {
    const response = await api.get('/content/templates', { params });
    return response.data;
  },

  /**
   * Get template by ID
   */
  getTemplate: async (templateId) => {
    const response = await api.get(`/content/templates/${templateId}`);
    return response.data;
  },

  /**
   * Create template
   */
  createTemplate: async (templateData) => {
    const response = await api.post('/content/templates', templateData);
    return response.data;
  },

  /**
   * Update template
   */
  updateTemplate: async (templateId, templateData) => {
    const response = await api.put(`/content/templates/${templateId}`, templateData);
    return response.data;
  },

  /**
   * Delete template
   */
  deleteTemplate: async (templateId) => {
    const response = await api.delete(`/content/templates/${templateId}`);
    return response.data;
  },

  /**
   * Preview template with data
   */
  previewTemplate: async (templateId, previewData) => {
    const response = await api.post(`/content/templates/${templateId}/preview`, previewData);
    return response.data;
  },

  // ==================== Metadata ====================
  /**
   * Get available platforms
   */
  getPlatforms: async () => {
    const response = await api.get('/content/platforms');
    return response.data;
  },

  /**
   * Get content types
   */
  getTypes: async () => {
    const response = await api.get('/content/types');
    return response.data;
  },

  // ==================== Analytics ====================
  /**
   * Get content analytics overview
   */
  getAnalyticsOverview: async (params = {}) => {
    const response = await api.get('/content/analytics/overview', { params });
    return response.data;
  },

  /**
   * Get piece analytics
   */
  getPieceAnalytics: async (pieceId, params = {}) => {
    const response = await api.get(`/content/pieces/${pieceId}/analytics`, { params });
    return response.data;
  },

  // ==================== AI Generation ====================
  /**
   * Generate content with AI
   */
  generateContent: async (generationConfig) => {
    const response = await api.post('/content/generate', generationConfig);
    return response.data;
  },

  /**
   * Improve content with AI
   */
  improveContent: async (pieceId, improvementConfig) => {
    const response = await api.post(`/content/pieces/${pieceId}/improve`, improvementConfig);
    return response.data;
  },

  /**
   * Translate content
   */
  translateContent: async (pieceId, targetLanguage) => {
    const response = await api.post(`/content/pieces/${pieceId}/translate`, {
      target_language: targetLanguage
    });
    return response.data;
  },

  // ==================== Scheduling ====================
  /**
   * Schedule content piece
   */
  schedulePiece: async (pieceId, scheduleData) => {
    const response = await api.post(`/content/pieces/${pieceId}/schedule`, scheduleData);
    return response.data;
  },

  /**
   * Unschedule content piece
   */
  unschedulePiece: async (pieceId) => {
    const response = await api.delete(`/content/pieces/${pieceId}/schedule`);
    return response.data;
  },

  /**
   * Publish content piece
   */
  publishPiece: async (pieceId, platforms = []) => {
    const response = await api.post(`/content/pieces/${pieceId}/publish`, { platforms });
    return response.data;
  }
};

export default contentService;
